

import { joinPersonName } from '@/utils/names';
import { Users, Loader2, Pencil, Trash2 } from 'lucide-react';

import { formatDate } from '@/utils/formatters';

import { useContactsController } from './useContactsController';
export function ContactListTable({ ctx }: { ctx: ReturnType<typeof useContactsController> }) {
const { crm, navigate, contacts, loading, error, page, setPage, total, limit, totalPages, fetchContacts, handleSort, SortIcon, openEdit, openDelete } = ctx;
return (<><div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading contacts...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchContacts} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Users size={40} className="mb-2" />
            <p className="text-sm">No contacts found</p>
            <p className="text-xs">Add your first contact to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('firstName')}>
                    <span className="flex items-center gap-1">Name <SortIcon field="firstName" /></span>
                  </th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Product Interest</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('source')}>
                    <span className="flex items-center gap-1">Source <SortIcon field="source" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('leadScore')}>
                    <span className="flex items-center gap-1">Lead Score <SortIcon field="leadScore" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('createdAt')}>
                    <span className="flex items-center gap-1">Created <SortIcon field="createdAt" /></span>
                  </th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map((c) => (
                  <tr
                    key={c.id}
                    onClick={e => crm ? openEdit(c, e) : navigate(`/contacts/${c.id}`)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <span className="flex items-center gap-2">
                        {joinPersonName(c.firstName, c.lastName)}
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${c.customerType === 'b2b' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                          {c.customerType === 'b2b' ? 'B2B' : 'B2C'}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.email}</td>
                    {/* BUG-C03 fix (Wave-3 May 1 2026): phone as tel: link */}
                    <td className="px-4 py-3 text-gray-600">
                      {c.phone
                        ? <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="hover:underline">{c.phone}</a>
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.productInterest ? (
                        <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
                          {c.productInterest}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.city || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.account?.name || '-'}</td>
                    <td className="px-4 py-3">
                      {c.source ? (
                        <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                          {c.source}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {c.leadScore != null ? (
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-16 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-[var(--ink-0)]"
                              style={{ width: `${Math.min(c.leadScore, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{c.leadScore}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => openEdit(c, e)}
                          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-[var(--ink-0)] transition-colors"
                          title="Edit contact"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={(e) => openDelete(c, e)}
                          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete contact"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      </>);
}
